/**
 * ContactSeoMeta.js
 *
 * SEO + structured data components for both Contact Us pages:
 *   - <ContactGeneralMeta />  — used in ContactUsForm (/contactUs)
 *   - <ContactPrenatalMeta /> — used in ContactUSPrenatal (/prenatalQuery)
 *
 * Mirrors the exact pattern of BlogSeoMeta.js / PackagesSeoMeta.js:
 *   - Keyed <Helmet> so React always re-renders on canonical change
 *   - trimDesc() respects word boundaries (155 char cap)
 *   - BreadcrumbList JSON-LD
 *   - ContactPage JSON-LD (general) / MedicalBusiness JSON-LD (prenatal)
 *   - Open Graph + Twitter Card
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@yogashastra";
const PHONE          = "+917838866331";

// ─── Helpers ───────────────────────────────────────────────────────────────────
// Mirrors BlogSeoMeta — word-boundary aware trim
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// ContactGeneralMeta — used on /contactUs
// ═══════════════════════════════════════════════════════════════════════════════

export const ContactGeneralMeta = () => {
  const canonical   = `${SITE_URL}/contact`;
  const title       = `Contact Us | ${SITE_NAME}`;
  const description = trimDesc(
    "Ready to begin your yoga journey? Fill in your details and one of our " +
    "experienced instructors will reach out to guide you toward the perfect " +
    "practice for your goals."
  );
  const keywords =
    "contact yoga shastra, yoga enquiry, book yoga class, yoga trial, " +
    "online yoga contact, yoga instructor india";

  // ── BreadcrumbList ───────────────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",       item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Contact Us", item: canonical },
    ],
  };

  // ── ContactPage ───────────────────────────────────────────────────────────────
  const contactSchema = {
    "@context":  "https://schema.org",
    "@type":     "ContactPage",
    name:         `Contact ${SITE_NAME}`,
    description,
    url:          canonical,
    mainEntity: {
      "@type":     "Organization",
      name:         SITE_NAME,
      url:          SITE_URL,
      logo:        { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
      telephone:    PHONE,
      contactPoint: {
        "@type":        "ContactPoint",
        telephone:       PHONE,
        contactType:    "customer support",
        availableLanguage: ["English", "Hindi"],
        areaServed:     "IN",
      },
    },
  };

  // Keyed on canonical — mirrors BlogSeoMeta
  return (
    <Helmet key={canonical}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords"    content={keywords} />
      <link rel="canonical"    href={canonical} />

      {/* Open Graph */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
    </Helmet>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ContactPrenatalMeta — used on /prenatalQuery
// ═══════════════════════════════════════════════════════════════════════════════

export const ContactPrenatalMeta = () => {
  const canonical   = `${SITE_URL}/prenatal-enquiry`;
  const title       = `Prenatal & Postnatal Yoga Enquiry | ${SITE_NAME}`;
  const description = trimDesc(
    "Whether you're expecting or recovering, our certified instructors design " +
    "personalised yoga sessions to support your body and mind through every " +
    "stage of motherhood. Book a session today."
  );
  const keywords =
    "prenatal yoga, postnatal yoga, maternal yoga, pregnancy yoga classes, " +
    "postpartum yoga, yoga for mothers, yoga shastra prenatal";

  // ── BreadcrumbList ───────────────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",           item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Prenatal Yoga",  item: canonical },
    ],
  };

  // ── MedicalBusiness — appropriate for a specialist maternal wellness service ─
  const medicalSchema = {
    "@context":   "https://schema.org",
    "@type":      ["MedicalBusiness", "HealthAndBeautyBusiness"],
    name:          `${SITE_NAME} — Prenatal & Postnatal Yoga`,
    url:           canonical,
    telephone:     PHONE,
    description,
    image:         OG_IMAGE,
    logo:         { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    medicalSpecialty: "Obstetrics",
    availableService: [
      {
        "@type":       "MedicalTherapy",
        name:           "Prenatal Yoga",
        description:   "Personalised yoga sessions for expectant mothers.",
        url:            canonical,
      },
      {
        "@type":       "MedicalTherapy",
        name:           "Postnatal Yoga",
        description:   "Recovery and restoration yoga for new mothers.",
        url:            canonical,
      },
    ],
    contactPoint: {
      "@type":           "ContactPoint",
      telephone:          PHONE,
      contactType:       "customer support",
      availableLanguage: ["English", "Hindi"],
      areaServed:        "IN",
    },
  };

  // Keyed on canonical — mirrors BlogSeoMeta
  return (
    <Helmet key={canonical}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords"    content={keywords} />
      <link rel="canonical"    href={canonical} />

      {/* Open Graph */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(medicalSchema)}</script>
    </Helmet>
  );
};